import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { Box } from "@mui/material"
import { useTheme } from "@mui/styles"

import ModalBase from "../modal-base"
import ModalMessage from "../modal-message"
import ModalExceed418 from "../modal-exceed-418"
import { ModalContent, ModalButtonWrapper, Close } from "../common"
import { ButtonWithImage } from "@components/buttons"
import { Column, Row, Text, VSpace, HSpace } from "@layout"
import { InputSearch } from "@components/inputs"
import { CheckboxBase } from "@components/checkbox"

import { getOrderTMsById, postCheck418PreCreate } from "@slices/orders"
import { t } from "i18next"
import { isEmpty } from "@utils/validator"

// [Service Package] a package order appoints a TM per sub-order. Eligibility is
// service-dependent, so candidates must be fetched per session with that session's
// own serviceId — a whole-package query would return the wrong list for the other
// item's sessions.
const buildSessionPayload = (createPayload, session, keyword) => ({
  customerId: createPayload?.customerId,
  serviceType: createPayload?.serviceType,
  serviceId: session.serviceId,
  sessions: [{ date: session.date, time: session.time, duration: session.duration }],
  keyword
})

const toCheck418Sessions = (sessions) =>
  sessions.map(({ date, time, duration }) => ({ date, time, duration }))

const ModalAppointPackage = ({ open, onClose, onConfirm, sessions = [], assignments = {}, createPayload }) => {
  const dispatch = useDispatch()
  const theme = useTheme()

  const [activeIndex, setActiveIndex] = useState(0)
  const [search, setSearch] = useState("")
  const [picked, setPicked] = useState({})
  const [candidates, setCandidates] = useState([])

  const [errorMessage, setErrorMessage] = useState()
  const [modalExceed418Open, setModalExceed418Open] = useState(false)

  // per-session candidate cache, keyed by `${index}|${keyword}` so switching
  // sessions back and forth doesn't re-query
  const candidateCacheRef = useRef({})
  // generation counter: an uncached session switched away from quickly can still have a
  // request in flight, and it must not land after the newer one and paint its
  // candidates under the current session's header
  const fetchGenRef = useRef(0)
  // the (index, keyword) pair currently displayed, so a re-triggered effect does not
  // re-request what is already on screen
  const lastLoadRef = useRef(null)

  useEffect(() => {
    if (!open) return
    candidateCacheRef.current = {}
    lastLoadRef.current = null
    setCandidates([])
    setSearch("")
    setPicked(assignments || {})
    setActiveIndex(0)
    // the first load belongs here rather than in the effect below: that one runs in
    // this same commit, before these setStates apply, so it would fire a request for
    // whichever session and keyword were active when the modal was last closed
    if (sessions.length) loadCandidates(0, "")
  }, [open])

  useEffect(() => {
    if (!open || !sessions.length) return
    loadCandidates(activeIndex, search)
  }, [activeIndex])

  const fetchCandidates = async (index, keyword = "") => {
    const session = sessions[index]
    if (!session) return []

    const cacheKey = `${index}|${keyword}`
    if (candidateCacheRef.current[cacheKey]) return candidateCacheRef.current[cacheKey]

    try {
      const { results } = await dispatch(
        getOrderTMsById(buildSessionPayload(createPayload, session, keyword))
      ).unwrap()
      candidateCacheRef.current[cacheKey] = results || []
      return results || []
    } catch (error) {
      // surface the backend message (e.g. ORDER_SESSIONS_OVERLAP) rather than
      // silently showing an empty list
      setErrorMessage(error?.data || { message: t("ERROR_COMMON_input_correct") })
      return []
    }
  }

  // the only path that paints the candidate list — every caller goes through here so
  // the staleness guard cannot be bypassed
  const loadCandidates = (index, keyword = "") => {
    const loadKey = `${index}|${keyword}`
    if (lastLoadRef.current === loadKey) return
    lastLoadRef.current = loadKey

    const gen = ++fetchGenRef.current
    fetchCandidates(index, keyword).then((results) => {
      if (fetchGenRef.current === gen) setCandidates(results)
    })
  }

  const dispatchCheck418 = async (tmUserId, checkSessions) => {
    if (!tmUserId || !checkSessions.length) return
    try {
      // a multi-session request is under the limit only if EVERY session's week
      // passes. 418 never blocks the order — the admin just has to see the warning
      const result = await dispatch(
        postCheck418PreCreate({ tmUserId, sessions: toCheck418Sessions(checkSessions) })
      ).unwrap()
      if (!result?.under418Limit) setModalExceed418Open(true)
    } catch (error) {
      setErrorMessage(error?.data || { message: t("ERROR_COMMON_input_correct") })
    }
  }

  const handleSearch = () => loadCandidates(activeIndex, search)

  const handlePick = (tmUser) => {
    const isSame = picked[activeIndex] === tmUser.id
    setPicked((prev) => ({ ...prev, [activeIndex]: isSame ? null : tmUser.id }))
    if (!isSame) dispatchCheck418(tmUser.id, [sessions[activeIndex]])
  }

  // an "apply to all" is only valid if the TM is eligible for EVERY session, so
  // each session's candidate list is checked before anything is assigned.
  // The re-check searches by phone (unique, and matched by findEligibleTmUsers'
  // keyword): an unfiltered query is capped at 20 rows server-side, which would drop
  // the picked TM on any real roster and report a false "not eligible".
  const handleApplyAll = async () => {
    const tmUserId = picked[activeIndex]
    const tmUser = candidates.find((item) => item.id === tmUserId)
    if (!tmUser) return

    const lists = await Promise.all(sessions.map((_, index) => fetchCandidates(index, tmUser.phone)))
    const ineligible = lists
      .map((list, index) => (list.some((item) => item.id === tmUserId) ? null : index + 1))
      .filter(Boolean)

    if (ineligible.length) {
      setErrorMessage({
        message: t("ERROR_APPOINT_TM_not_eligible_sessions", { sessions: ineligible.join("、") })
      })
      return
    }

    const next = {}
    sessions.forEach((_, index) => {
      next[index] = tmUserId
    })
    setPicked(next)
    dispatchCheck418(tmUserId, sessions)
  }

  const handleConfirm = () => {
    onConfirm({ assignments: picked })
  }

  const activeTm = candidates.find((item) => item.id === picked[activeIndex])
  const tmLabel = (item) => `${item.nameZh}#${item.phone}`

  const buttonList = [{ label: t("TEXT_COMMON_confirm"), srcx: "save-black", onClick: handleConfirm }]

  const modalMessageConfig = {
    errorMessage,
    open: !isEmpty(errorMessage),
    onClose: () => setErrorMessage()
  }

  const modalExceed418Config = {
    open: modalExceed418Open,
    onClose: () => setModalExceed418Open(false),
    onCancal: () => setPicked((prev) => ({ ...prev, [activeIndex]: null }))
  }

  return (
    <>
      <ModalBase open={open} onClose={onClose}>
        <ModalContent expand maxwidth="1040px">
          <Close onClick={onClose} />
          <Column sx={{ padding: "22px", height: "-webkit-fill-available", overflow: "hidden" }}>
            <Text size="18px" weight="500">
              {t("MODAL_Manual_Match_appoint_tm")}
            </Text>
            <VSpace length="15" />
            <Row align="stretch" sx={{ gap: "22px", flex: 1, minHeight: 0 }}>
              <Column
                sx={{
                  width: "300px",
                  minWidth: "300px",
                  border: `1px solid ${theme.border.gray_3}`,
                  borderRadius: "12px",
                  overflow: "hidden"
                }}
              >
                <Box sx={{ backgroundColor: theme.palette.background.gray_2, padding: "12px 16px" }}>
                  <Row>
                    <Text size="15px" weight="500">
                      {t("MODAL_Appoint_TM_select_session")}
                    </Text>
                    <HSpace length="6" />
                    <Text size="13px" color="gray_9">
                      {t("MODAL_Appoint_TM_total_sessions", { count: sessions.length })}
                    </Text>
                  </Row>
                </Box>
                <Column sx={{ overflowY: "auto" }}>
                  {sessions.map((session, index) => {
                    const assignedId = picked[index]
                    const isActive = index === activeIndex
                    return (
                      <Box
                        key={`appoint_session_${index}`}
                        onClick={() => setActiveIndex(index)}
                        sx={{
                          cursor: "pointer",
                          padding: "13px 16px",
                          borderBottom: `1px solid ${theme.border.gray_10}`,
                          borderLeft: `4px solid ${isActive ? theme.border.yellow : "transparent"}`,
                          backgroundColor: isActive ? theme.palette.background.gray_2 : "transparent"
                        }}
                      >
                        <Row>
                          <Text size="14px" weight="500">
                            {t("ORDER_Label_session_no", { index: index + 1 })}
                          </Text>
                          <HSpace length="6" />
                          <Text size="13px" color="gray_9">
                            {session.serviceName}
                          </Text>
                        </Row>
                        <Text size="13px" color={assignedId ? "green" : "gray_9"}>
                          {assignedId
                            ? sessionAssignedLabel(assignedId, candidateCacheRef.current, tmLabel)
                            : t("MODAL_Appoint_TM_unassigned")}
                        </Text>
                      </Box>
                    )
                  })}
                </Column>
              </Column>

              <Column sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                {activeTm ? (
                  <>
                    <Text size="15px" weight="500">
                      {t("MODAL_Manual_Match_selected_match")}
                    </Text>
                    <VSpace length="8" />
                    <Row
                      sx={{
                        border: `1px solid ${theme.border.gray_3}`,
                        borderRadius: "12px",
                        padding: "14px 18px",
                        justifyContent: "space-between"
                      }}
                    >
                      <Column>
                        <Text size="15px" weight="500">
                          {tmLabel(activeTm)}
                        </Text>
                        <Text size="14px" color="gray_9">
                          {t("MODAL_Appoint_TM_tm_meta", {
                            rating: isEmpty(activeTm.rating) ? "-" : activeTm.rating / 100,
                            group: activeTm.assignedGroup ? t(`OPTION_COMMON_team_${activeTm.assignedGroup}`) : "-"
                          })}
                        </Text>
                      </Column>
                      <ButtonWithImage
                        label={t("MODAL_Appoint_TM_apply_all")}
                        onClick={handleApplyAll}
                        color="primary_dark"
                        width="160px"
                      />
                    </Row>
                    <VSpace length="16" />
                  </>
                ) : null}

                <InputSearch
                  width="100%"
                  value={search}
                  placeholder={t("MODAL_Appoint_TM_search_placeholder")}
                  handleChange={setSearch}
                  handleSearch={handleSearch}
                />
                <VSpace length="15" />
                <Column sx={{ overflowY: "auto" }}>
                  {candidates.map((item) => (
                    <Row key={`appoint_tm_${item.id}`} sx={{ margin: "10px 0" }}>
                      <CheckboxBase
                        round={false}
                        label={tmLabel(item)}
                        onChange={() => handlePick(item)}
                        checked={picked[activeIndex] === item.id}
                      />
                    </Row>
                  ))}
                </Column>
              </Column>
            </Row>
          </Column>
          <ModalButtonWrapper color="yellow">
            {buttonList.map(({ onClick, ...btnProp }) => (
              <ButtonWithImage
                textcolor="brown_1"
                sx={{ width: "115px" }}
                key={`modal button ${btnProp.label}`}
                onClick={onClick}
                {...btnProp}
              />
            ))}
          </ModalButtonWrapper>
        </ModalContent>
      </ModalBase>
      <ModalMessage {...modalMessageConfig} />
      <ModalExceed418 {...modalExceed418Config} />
    </>
  )
}

// the assigned TM may live in another session's cached list, so look across all of them
const sessionAssignedLabel = (tmUserId, cache, tmLabel) => {
  for (const list of Object.values(cache)) {
    const found = list.find((item) => item.id === tmUserId)
    if (found) return tmLabel(found)
  }
  return ""
}

export default ModalAppointPackage
